/**
 * Gamification Components
 * Unified gamification UI elements for the entire training platform
 */

import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    Trophy, Star, Flame, Target, Zap, Award, Medal,
    TrendingUp, CheckCircle2, Sparkles, Crown, Gift,
    BookOpen, Mic, Video, Briefcase, Users, MessageCircle,
    Bell, Calendar, AlertCircle
} from 'lucide-react';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';

// ============================================================================
// BADGE ICON MAPPER
// ============================================================================

const BADGE_ICONS = {
    'message-circle': MessageCircle,
    'repeat': TrendingUp,
    'award': Award,
    'star': Star,
    'briefcase': Briefcase,
    'users': Users,
    'handshake': Medal,
    'crown': Crown,
    'trophy': Trophy,
    'flame': Flame,
    'zap': Zap,
    'target': Target,
    'book': BookOpen,
    'mic': Mic,
    'video': Video,
    'sparkles': Sparkles,
    'gift': Gift,
    'check': CheckCircle2
};

const TIER_COLORS = {
    bronze: { bg: 'bg-amber-600/20', border: 'border-amber-600', text: 'text-amber-600', glow: 'shadow-amber-500/30' },
    silver: { bg: 'bg-slate-400/20', border: 'border-slate-400', text: 'text-slate-400', glow: 'shadow-slate-400/30' },
    gold: { bg: 'bg-yellow-500/20', border: 'border-yellow-500', text: 'text-yellow-500', glow: 'shadow-yellow-500/30' },
    platinum: { bg: 'bg-cyan-400/20', border: 'border-cyan-400', text: 'text-cyan-400', glow: 'shadow-cyan-400/30' },
    diamond: { bg: 'bg-violet-500/20', border: 'border-violet-500', text: 'text-violet-500', glow: 'shadow-violet-500/30' }
};

// ============================================================================
// ACHIEVEMENT POPUP - Shows when user earns a badge
// ============================================================================

export const AchievementPopup = ({ badge, onClose, autoClose = 5000 }) => {
    useEffect(() => {
        // Trigger confetti
        confetti({
            particleCount: 100,
            spread: 70,
            origin: { y: 0.6 }
        });
        
        if (autoClose) {
            const timer = setTimeout(onClose, autoClose);
            return () => clearTimeout(timer);
        }
    }, [autoClose, onClose]);

    const Icon = BADGE_ICONS[badge.icon] || Award;
    const tierStyle = TIER_COLORS[badge.tier] || TIER_COLORS.bronze;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0, scale: 0.5, y: -50 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.5, y: -50 }}
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
                onClick={onClose}
            >
                <motion.div
                    initial={{ rotateY: 180 }}
                    animate={{ rotateY: 0 }}
                    transition={{ duration: 0.6, type: "spring" }}
                    className={`relative p-8 rounded-2xl ${tierStyle.bg} border-2 ${tierStyle.border} shadow-2xl ${tierStyle.glow}`}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                        <motion.div
                            animate={{ y: [0, -5, 0] }}
                            transition={{ repeat: Infinity, duration: 1.5 }}
                        >
                            <Sparkles className={`h-8 w-8 ${tierStyle.text}`} />
                        </motion.div>
                    </div>
                    
                    <div className="text-center">
                        <motion.div
                            animate={{ rotate: [0, 10, -10, 0] }}
                            transition={{ repeat: Infinity, duration: 2 }}
                            className={`w-24 h-24 mx-auto rounded-full ${tierStyle.bg} border-4 ${tierStyle.border} flex items-center justify-center mb-4`}
                        >
                            <Icon className={`h-12 w-12 ${tierStyle.text}`} />
                        </motion.div>
                        
                        <h2 className="text-2xl font-bold text-white mb-2">Achievement Unlocked!</h2>
                        <h3 className={`text-xl font-semibold ${tierStyle.text} mb-2`}>{badge.name}</h3>
                        <p className="text-white/70 mb-4 max-w-xs">{badge.description}</p>
                        
                        <div className="flex items-center justify-center gap-2">
                            <Badge className={`${tierStyle.bg} ${tierStyle.text} border ${tierStyle.border}`}>
                                +{badge.points} XP
                            </Badge>
                            <Badge variant="outline" className="text-white/70">
                                {badge.tier.toUpperCase()}
                            </Badge>
                        </div>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

// ============================================================================
// SCORE ANIMATION - Animated score display with ring
// ============================================================================

export const AnimatedScore = ({ score, maxScore = 100, size = 'md', showLabel = true, label = 'Score' }) => {
    const [displayScore, setDisplayScore] = useState(0);
    const percentage = Math.round((score / maxScore) * 100);
    
    const sizes = {
        sm: { ring: 80, stroke: 6, text: 'text-xl', label: 'text-xs' },
        md: { ring: 120, stroke: 8, text: 'text-3xl', label: 'text-sm' },
        lg: { ring: 160, stroke: 10, text: 'text-4xl', label: 'text-base' }
    };
    
    const { ring, stroke, text, label: labelSize } = sizes[size] || sizes.md;
    const radius = (ring - stroke) / 2;
    const circumference = radius * 2 * Math.PI;
    const offset = circumference - (displayScore / 100) * circumference;
    
    useEffect(() => {
        const duration = 1500;
        const start = Date.now();
        
        const animate = () => {
            const elapsed = Date.now() - start;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3); // Ease out cubic
            
            setDisplayScore(Math.round(percentage * eased));
            
            if (progress < 1) {
                requestAnimationFrame(animate);
            }
        };
        
        requestAnimationFrame(animate);
    }, [percentage]);
    
    const getScoreColor = () => {
        if (displayScore >= 90) return { stroke: '#10b981', text: 'text-emerald-500' };
        if (displayScore >= 70) return { stroke: '#3b82f6', text: 'text-blue-500' };
        if (displayScore >= 50) return { stroke: '#f59e0b', text: 'text-amber-500' };
        return { stroke: '#ef4444', text: 'text-red-500' };
    };
    
    const colors = getScoreColor();
    
    return (
        <div className="relative inline-flex items-center justify-center">
            <svg width={ring} height={ring} className="transform -rotate-90">
                <circle
                    cx={ring / 2}
                    cy={ring / 2}
                    r={radius}
                    fill="transparent"
                    stroke="currentColor"
                    strokeWidth={stroke}
                    className="text-white/10"
                />
                <motion.circle
                    cx={ring / 2}
                    cy={ring / 2}
                    r={radius}
                    fill="transparent"
                    stroke={colors.stroke}
                    strokeWidth={stroke}
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    initial={{ strokeDashoffset: circumference }}
                    animate={{ strokeDashoffset: offset }}
                    transition={{ duration: 1.5, ease: "easeOut" }}
                />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className={`font-bold ${text} ${colors.text}`}>
                    {displayScore}%
                </span>
                {showLabel && (
                    <span className={`${labelSize} text-white/50`}>{label}</span>
                )}
            </div>
        </div>
    );
};

// ============================================================================
// STREAK INDICATOR - Shows current streak with fire animation
// ============================================================================

export const StreakIndicator = ({ streak, showDetails = false }) => {
    const getStreakTier = () => {
        if (streak >= 30) return { tier: 'Legendary', color: 'text-violet-500', bg: 'bg-violet-500/20' };
        if (streak >= 14) return { tier: 'Epic', color: 'text-orange-500', bg: 'bg-orange-500/20' };
        if (streak >= 7) return { tier: 'Hot', color: 'text-red-500', bg: 'bg-red-500/20' };
        if (streak >= 3) return { tier: 'Warm', color: 'text-amber-500', bg: 'bg-amber-500/20' };
        return { tier: 'Starting', color: 'text-slate-400', bg: 'bg-slate-400/20' };
    };
    
    const { tier, color, bg } = getStreakTier();
    
    return (
        <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${bg}`}>
            <motion.div
                animate={{ scale: [1, 1.2, 1], rotate: [0, 5, -5, 0] }}
                transition={{ repeat: Infinity, duration: 1.5 }}
            >
                <Flame className={`h-5 w-5 ${color}`} />
            </motion.div>
            <span className={`font-bold ${color}`}>{streak}</span>
            {showDetails && (
                <span className="text-xs text-white/50">day streak</span>
            )}
        </div>
    );
};

// ============================================================================
// XP BAR - Shows XP progress with level
// ============================================================================

export const XPBar = ({ currentXP, level, xpToNextLevel }) => {
    const progress = (currentXP / xpToNextLevel) * 100;
    
    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                        <span className="text-sm font-bold text-white">{level}</span>
                    </div>
                    <span className="text-sm text-white/70">Level {level}</span>
                </div>
                <span className="text-xs text-white/50">
                    {currentXP.toLocaleString()} / {xpToNextLevel.toLocaleString()} XP
                </span>
            </div>
            <div className="relative h-2 bg-white/10 rounded-full overflow-hidden">
                <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 1, ease: "easeOut" }}
                    className="absolute h-full bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full"
                />
            </div>
        </div>
    );
};

// ============================================================================
// BADGE DISPLAY - Shows a badge with hover effects
// ============================================================================

export const BadgeDisplay = ({ badge, earned = false, onClick }) => {
    const Icon = BADGE_ICONS[badge.icon] || Award;
    const tierStyle = TIER_COLORS[badge.tier] || TIER_COLORS.bronze;
    
    return (
        <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onClick}
            className={`
                relative p-4 rounded-xl cursor-pointer transition-all
                ${earned 
                    ? `${tierStyle.bg} border-2 ${tierStyle.border} shadow-lg ${tierStyle.glow}` 
                    : 'bg-white/5 border border-white/10 opacity-50 grayscale'
                }
            `}
        >
            {!earned && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-xl">
                    <span className="text-xs text-white/50">Locked</span>
                </div>
            )}
            
            <div className="text-center">
                <div className={`w-12 h-12 mx-auto rounded-full ${earned ? tierStyle.bg : 'bg-white/10'} flex items-center justify-center mb-2`}>
                    <Icon className={`h-6 w-6 ${earned ? tierStyle.text : 'text-white/30'}`} />
                </div>
                <p className={`text-sm font-medium ${earned ? 'text-white' : 'text-white/50'}`}>
                    {badge.name}
                </p>
                <p className="text-xs text-white/40 mt-1">{badge.points} XP</p>
            </div>
        </motion.div>
    );
};

// ============================================================================
// REAL-TIME FEEDBACK PULSE - For voice recording feedback
// ============================================================================

export const VoiceFeedbackPulse = ({ isActive, quality = 'good' }) => {
    const qualityColors = {
        excellent: 'from-emerald-500 to-green-500',
        good: 'from-blue-500 to-cyan-500',
        fair: 'from-amber-500 to-yellow-500',
        poor: 'from-red-500 to-orange-500'
    };
    
    const gradient = qualityColors[quality] || qualityColors.good;
    
    return (
        <div className="relative flex items-center justify-center">
            {isActive && (
                <>
                    <motion.div
                        animate={{ scale: [1, 1.5, 1], opacity: [0.5, 0, 0.5] }}
                        transition={{ repeat: Infinity, duration: 1.5 }}
                        className={`absolute w-16 h-16 rounded-full bg-gradient-to-r ${gradient} opacity-50`}
                    />
                    <motion.div
                        animate={{ scale: [1, 1.3, 1], opacity: [0.7, 0.3, 0.7] }}
                        transition={{ repeat: Infinity, duration: 1, delay: 0.2 }}
                        className={`absolute w-12 h-12 rounded-full bg-gradient-to-r ${gradient} opacity-70`}
                    />
                </>
            )}
            <div className={`relative w-8 h-8 rounded-full bg-gradient-to-r ${gradient} flex items-center justify-center`}>
                <Mic className="h-4 w-4 text-white" />
            </div>
        </div>
    );
};

// ============================================================================
// SESSION PROGRESS TRACKER
// ============================================================================

export const SessionProgressTracker = ({ 
    stages, 
    currentStage, 
    completedStages = [],
    showLabels = true 
}) => {
    return (
        <div className="flex items-center gap-2 w-full">
            {stages.map((stage, index) => {
                const isCompleted = completedStages.includes(stage.id);
                const isCurrent = currentStage === stage.id;
                
                return (
                    <div key={stage.id} className="flex items-center flex-1">
                        <div className="flex flex-col items-center">
                            <motion.div
                                animate={isCurrent ? { scale: [1, 1.1, 1] } : {}}
                                transition={{ repeat: Infinity, duration: 2 }}
                                className={`
                                    w-10 h-10 rounded-full flex items-center justify-center transition-all
                                    ${isCompleted 
                                        ? 'bg-emerald-500 text-white' 
                                        : isCurrent 
                                            ? 'bg-indigo-500 text-white ring-4 ring-indigo-500/30' 
                                            : 'bg-white/10 text-white/30'
                                    }
                                `}
                            >
                                {isCompleted ? (
                                    <CheckCircle2 className="h-5 w-5" />
                                ) : (
                                    <span className="text-sm font-bold">{index + 1}</span>
                                )}
                            </motion.div>
                            {showLabels && (
                                <span className={`text-xs mt-1 ${isCurrent ? 'text-white' : 'text-white/50'}`}>
                                    {stage.label}
                                </span>
                            )}
                        </div>
                        {index < stages.length - 1 && (
                            <div className={`flex-1 h-1 mx-2 rounded ${isCompleted ? 'bg-emerald-500' : 'bg-white/10'}`} />
                        )}
                    </div>
                );
            })}
        </div>
    );
};

// ============================================================================
// LIVE SCORE CARD - Shows real-time scoring during sessions
// ============================================================================

export const LiveScoreCard = ({ 
    voiceScore, 
    communicationScore, 
    overallScore,
    showBreakdown = true 
}) => {
    return (
        <Card className="bg-gradient-to-br from-slate-900/90 to-slate-800/90 border-white/10">
            <CardContent className="p-4">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-medium text-white/70">Live Score</h3>
                    <Badge variant="outline" className="text-emerald-400 border-emerald-400/30">
                        Real-time
                    </Badge>
                </div>
                
                <div className="flex items-center justify-center mb-4">
                    <AnimatedScore score={overallScore} size="md" label="Overall" />
                </div>
                
                {showBreakdown && (
                    <div className="grid grid-cols-2 gap-4 pt-4 border-t border-white/10">
                        <div className="text-center">
                            <div className="flex items-center justify-center gap-1 mb-1">
                                <Mic className="h-4 w-4 text-blue-400" />
                                <span className="text-xs text-white/50">Voice</span>
                            </div>
                            <span className="text-lg font-bold text-blue-400">{voiceScore}%</span>
                        </div>
                        <div className="text-center">
                            <div className="flex items-center justify-center gap-1 mb-1">
                                <MessageCircle className="h-4 w-4 text-purple-400" />
                                <span className="text-xs text-white/50">Content</span>
                            </div>
                            <span className="text-lg font-bold text-purple-400">{communicationScore}%</span>
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

// ============================================================================
// RESUME BANNER - Shows when user has an incomplete session
// ============================================================================

export const ResumeBanner = ({ sessionName, progress, lastActive, onResume, onStartNew }) => {
    return (
        <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-indigo-600/20 to-purple-600/20 border border-indigo-500/30 rounded-xl p-4 mb-6"
        >
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-indigo-500/20 flex items-center justify-center">
                        <BookOpen className="h-6 w-6 text-indigo-400" />
                    </div>
                    <div>
                        <h3 className="font-medium text-white">Continue where you left off?</h3>
                        <p className="text-sm text-white/60">
                            {sessionName} • {progress}% complete • Last active {lastActive}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={onStartNew}
                        className="px-4 py-2 text-sm text-white/70 hover:text-white transition-colors"
                    >
                        Start New
                    </button>
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={onResume}
                        className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors"
                    >
                        Resume Session
                    </motion.button>
                </div>
            </div>
        </motion.div>
    );
};

// ============================================================================
// STREAK NOTIFICATION - Shows streak status and reminders
// ============================================================================

export const StreakNotification = ({ streak, lastActivityDate, onDismiss }) => {
    const [show, setShow] = useState(true);
    
    const today = new Date().toDateString();
    const lastActivity = lastActivityDate ? new Date(lastActivityDate).toDateString() : null;
    const isStreakActive = lastActivity === today;
    const willLoseStreak = lastActivity && new Date(lastActivityDate).getTime() < Date.now() - 24 * 60 * 60 * 1000;
    
    if (!show || isStreakActive) return null;
    
    const handleDismiss = () => {
        setShow(false);
        onDismiss?.();
    };
    
    return (
        <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            className={`
                fixed top-4 right-4 z-50 max-w-sm p-4 rounded-xl shadow-lg border backdrop-blur-sm
                ${willLoseStreak 
                    ? 'bg-rose-500/90 border-rose-400/50 text-white' 
                    : 'bg-amber-500/90 border-amber-400/50 text-white'
                }
            `}
        >
            <div className="flex items-start gap-3">
                <motion.div
                    animate={{ scale: [1, 1.2, 1], rotate: [0, 10, -10, 0] }}
                    transition={{ repeat: Infinity, duration: 2 }}
                    className="flex-shrink-0"
                >
                    <Flame className="h-8 w-8" />
                </motion.div>
                <div className="flex-1">
                    <h4 className="font-semibold flex items-center gap-2">
                        {willLoseStreak ? 'Streak at Risk!' : 'Keep Your Streak Going!'}
                        {streak > 0 && (
                            <Badge className="bg-white/20 text-white">{streak} days</Badge>
                        )}
                    </h4>
                    <p className="text-sm mt-1 opacity-90">
                        {willLoseStreak 
                            ? "Complete any training activity today to save your streak!"
                            : "Don't forget to practice today to maintain your streak."
                        }
                    </p>
                    <div className="flex gap-2 mt-3">
                        <Button
                            size="sm"
                            variant="secondary"
                            className="bg-white/20 hover:bg-white/30 text-white border-0"
                            onClick={handleDismiss}
                        >
                            Remind Later
                        </Button>
                    </div>
                </div>
                <button 
                    onClick={handleDismiss}
                    className="text-white/70 hover:text-white"
                >
                    ×
                </button>
            </div>
        </motion.div>
    );
};

// ============================================================================
// VOICE RECORDING PROGRESS - Real-time visual feedback during recording
// ============================================================================

export const VoiceRecordingProgress = ({ 
    isRecording, 
    duration, 
    minDuration = 2,
    maxDuration = 120,
    quality = 'good' 
}) => {
    const progress = Math.min((duration / minDuration) * 100, 100);
    const totalProgress = (duration / maxDuration) * 100;
    
    const qualityColors = {
        excellent: { bar: 'bg-emerald-500', text: 'text-emerald-500', label: 'Excellent Quality' },
        good: { bar: 'bg-blue-500', text: 'text-blue-500', label: 'Good Quality' },
        fair: { bar: 'bg-amber-500', text: 'text-amber-500', label: 'Fair Quality' },
        poor: { bar: 'bg-rose-500', text: 'text-rose-500', label: 'Low Quality' }
    };
    
    const currentQuality = qualityColors[quality] || qualityColors.good;
    
    const formatTime = (secs) => {
        const mins = Math.floor(secs / 60);
        const s = secs % 60;
        return `${mins}:${s.toString().padStart(2, '0')}`;
    };
    
    if (!isRecording) return null;
    
    return (
        <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="p-4 rounded-xl bg-slate-900/80 border border-white/10 space-y-3"
        >
            {/* Recording Status */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <motion.div
                        animate={{ scale: [1, 1.2, 1] }}
                        transition={{ repeat: Infinity, duration: 1 }}
                        className="w-3 h-3 bg-rose-500 rounded-full"
                    />
                    <span className="text-white font-medium">Recording</span>
                </div>
                <span className="text-white/70 font-mono">{formatTime(duration)}</span>
            </div>
            
            {/* Progress to minimum */}
            {duration < minDuration && (
                <div className="space-y-1">
                    <div className="flex justify-between text-xs">
                        <span className="text-white/50">Minimum recording time</span>
                        <span className="text-white/70">{minDuration - duration}s remaining</span>
                    </div>
                    <Progress value={progress} className="h-2" />
                </div>
            )}
            
            {/* Quality Indicator */}
            {duration >= minDuration && (
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <VoiceFeedbackPulse isActive={true} quality={quality} />
                        <span className={`text-sm ${currentQuality.text}`}>{currentQuality.label}</span>
                    </div>
                    <Badge variant="outline" className="text-emerald-400 border-emerald-400/30">
                        Ready to submit
                    </Badge>
                </div>
            )}
            
            {/* Audio Waveform Visualization */}
            <div className="flex items-center justify-center gap-1 h-8">
                {[...Array(20)].map((_, i) => (
                    <motion.div
                        key={`wave-bar-${i}`}
                        animate={{ 
                            height: isRecording ? [4, Math.random() * 24 + 8, 4] : 4 
                        }}
                        transition={{ 
                            repeat: Infinity, 
                            duration: 0.5 + Math.random() * 0.3,
                            delay: i * 0.05 
                        }}
                        className={`w-1 rounded-full ${currentQuality.bar}`}
                        style={{ minHeight: 4 }}
                    />
                ))}
            </div>
        </motion.div>
    );
};

// ============================================================================
// DAILY GOAL TRACKER - Shows progress toward daily training goals
// ============================================================================

export const DailyGoalTracker = ({ completed, goal, streakDays, onViewDetails }) => {
    const progress = Math.min((completed / goal) * 100, 100);
    const isComplete = completed >= goal;
    
    return (
        <Card className="bg-gradient-to-br from-indigo-500/10 to-purple-500/10 border-indigo-500/20">
            <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                        <Target className="h-5 w-5 text-indigo-400" />
                        <span className="font-medium text-white">Daily Goal</span>
                    </div>
                    {streakDays > 0 && (
                        <div className="flex items-center gap-1 text-orange-400">
                            <Flame className="h-4 w-4" />
                            <span className="text-sm font-medium">{streakDays} day streak</span>
                        </div>
                    )}
                </div>
                
                <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                        <span className="text-white/70">{completed} of {goal} activities</span>
                        <span className={isComplete ? 'text-emerald-400' : 'text-white/70'}>
                            {isComplete ? 'Complete!' : `${goal - completed} remaining`}
                        </span>
                    </div>
                    <div className="relative h-3 bg-white/10 rounded-full overflow-hidden">
                        <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${progress}%` }}
                            transition={{ duration: 1, ease: "easeOut" }}
                            className={`absolute h-full rounded-full ${
                                isComplete 
                                    ? 'bg-gradient-to-r from-emerald-500 to-green-500' 
                                    : 'bg-gradient-to-r from-indigo-500 to-purple-500'
                            }`}
                        />
                    </div>
                </div>
                
                {isComplete && (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mt-3 p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-center"
                    >
                        <span className="text-sm text-emerald-400 flex items-center justify-center gap-2">
                            <CheckCircle2 className="h-4 w-4" />
                            Daily goal achieved! Keep it up!
                        </span>
                    </motion.div>
                )}
                
                {onViewDetails && (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onViewDetails}
                        className="w-full mt-3 text-white/70 hover:text-white"
                    >
                        View Progress Details
                    </Button>
                )}
            </CardContent>
        </Card>
    );
};

// ============================================================================
// LEADERBOARD MINI CARD - Compact leaderboard display for dashboards
// ============================================================================

export const LeaderboardMiniCard = ({ leaders, currentUserRank, onViewFull }) => {
    return (
        <Card className="bg-gradient-to-br from-yellow-500/5 to-amber-500/5 border-yellow-500/20">
            <CardContent className="p-4">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <Crown className="h-5 w-5 text-yellow-500" />
                        <span className="font-medium">Top Performers</span>
                    </div>
                    {currentUserRank && (
                        <Badge variant="outline" className="text-yellow-500 border-yellow-500/30">
                            Your Rank: #{currentUserRank}
                        </Badge>
                    )}
                </div>
                
                <div className="space-y-2">
                    {leaders?.slice(0, 5).map((leader, idx) => (
                        <div 
                            key={leader.id || idx}
                            className={`flex items-center gap-3 p-2 rounded-lg transition-colors ${
                                leader.isCurrentUser 
                                    ? 'bg-primary/10 ring-1 ring-primary/30' 
                                    : 'hover:bg-white/5'
                            }`}
                        >
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                idx === 0 ? 'bg-yellow-500 text-black' :
                                idx === 1 ? 'bg-slate-400 text-white' :
                                idx === 2 ? 'bg-amber-600 text-white' :
                                'bg-white/10 text-white/70'
                            }`}>
                                {idx + 1}
                            </div>
                            <span className="flex-1 text-sm truncate text-white/90">
                                {leader.name || 'Unknown'}
                            </span>
                            <span className="text-sm font-medium text-white/70">
                                {leader.points?.toLocaleString() || 0} XP
                            </span>
                        </div>
                    ))}
                </div>
                
                {onViewFull && (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onViewFull}
                        className="w-full mt-3 text-yellow-500/70 hover:text-yellow-500"
                    >
                        View Full Leaderboard
                    </Button>
                )}
            </CardContent>
        </Card>
    );
};

export default {
    AchievementPopup,
    AnimatedScore,
    StreakIndicator,
    XPBar,
    BadgeDisplay,
    VoiceFeedbackPulse,
    SessionProgressTracker,
    LiveScoreCard,
    ResumeBanner,
    StreakNotification,
    VoiceRecordingProgress,
    DailyGoalTracker,
    LeaderboardMiniCard
};
